import React, { useState } from 'react';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { useQuery } from 'react-query';
import Mixpanel from 'utils/mixpanelService';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import CloseIcon from '@material-ui/icons/Close';
import Box from '@material-ui/core/Box';
import Card from 'components/Card';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useContentfulLocale from 'components/useContentfulLocale';
import { getImageFile } from 'utils/stringUtils';
import { getEventPageName } from 'utils/trackingPageUtils';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import ContextInfoPopup from 'components/ContextInfoPopup';
import CopyButton from 'components/CopyButton';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import contentfulClient from 'utils/contentfulService';
import { EmailIcon } from './icons/EmailIcon';

const useStyles = makeStyles(theme => ({
  card: {
    boxShadow: props =>
      props.expanded
        ? '0px 2px 16px rgba(1, 97, 155, 0.5)'
        : '0px 2px 8px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    borderRadius: 8,
    transition: 'box-shadow 0.3s ease',
    minHeight: 300,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  imageContainer: {
    width: '100%',
    height: 80,
    position: 'relative',
    overflow: 'hidden',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  infoWrapper: {
    padding: '0 10px',
    marginTop: 14,
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...theme.typography.h2,
    paddingRight: 14,
    fontSize: 14,
    lineHeight: '18px',
  },
  description: {
    fontSize: 12,
    marginTop: 6,
    lineHeight: '14px',
  },
  highlightsContainer: {
    marginTop: 8,
  },
  exclusionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  exclusionIcon: {
    marginRight: 10,
    fontSize: 18,
    fontWeight: 'bold',
    strokeWidth: 3,
    stroke: theme.palette.ADACompliantRed,
  },
  exclusionText: {
    fontSize: 12,
    lineHeight: '14px',
    color: theme.palette.ADACompliantRed,
    textAlign: 'left',
    marginTop: 2,
  },
  buttonsSection: {
    marginTop: 'auto',
  },
  ctaWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: 10,
    marginTop: 16,
  },
  copyButton: {
    flex: 1,
    minWidth: '100px',
  },
  ctaButton: {
    ...theme.typography.pxSmall,
    borderRadius: 8,
    padding: '6px 12px',
    textTransform: 'none',
    lineHeight: '18px',
    flex: 1,
    textAlign: 'center',
    minWidth: '100px',
  },
  scheduleButton: {
    color: '#fff',
  },
  scheduleText: {
    marginRight: 5,
  },
  viewMoreButton: {
    borderRadius: 8,
    width: '100%',
    backgroundColor: '#EEF9FF',
    color: theme.palette.primary.main,
    padding: '8px',
    ...theme.typography.pxSmall,
    '&:hover': {
      backgroundColor: '#E0E9F0',
    },
  },
  itemWrapper: {
    position: 'relative',
    height: '100%',
    borderRadius: 8,
  },
  arrowIcon: {
    fontSize: 16,
    transform: props => (props.expanded ? 'rotate(180deg)' : 'none'),
    marginLeft: 4,
  },
}));

const getTextsFromRichText = richText => {
  if (!richText) return null;

  const html = documentToHtmlString(richText);
  const text = documentToPlainTextString(richText);

  return {
    html,
    text,
  };
};

// Fetch Context data by ID
const getContextData = async (_, contextId, localeFilters) => {
  const response = await contentfulClient.getEntry(contextId, {
    ...localeFilters,
  });
  return response;
};

function LinkItemMultiCta({ item, page = 'Home', grayscale }) {
  const [showPopup, setShowPopup] = useState(false);
  const classes = useStyles({ expanded: showPopup });
  const locale = useAlgoliaLocale();
  const ctfLocaleFilter = useContentfulLocale();

  const isTopicPage = page === 'Topic';

  const [resourceItemPageSiteCopy] = useSiteCopySelector([
    'resource-item-page',
  ]);

  const {
    slug,
    contactInfo,
    contactInfoLink,
    readMoreReference,
    contactInfoType,
    contactInfoCopy,
  } = item;

  const cardTitle =
    getAlgoliaLocalizedField(item, 'title', locale) ||
    getAlgoliaLocalizedField(item, 'name', locale);

  const cardSubtitle =
    getAlgoliaLocalizedField(item, 'subtitle', locale) ||
    getAlgoliaLocalizedField(item, 'shortDescription', locale);

  const trackEvent = (eventName, eventData) => {
    const pageName = getEventPageName();
    Mixpanel.track(eventName, {
      title: cardTitle,
      page: pageName,
      slug,
      ...(eventData || {}),
    });
  };

  const handleScheduleClick = e => {
    e.stopPropagation();
    // Schedule/Navigate logic would go here
    if (item.url) {
      trackEvent('Client Resource Card Exited - User-Resources');
      window.open(item.url, '_blank', 'noopener');
    }
  };

  const handleLinkClick = e => {
    e.stopPropagation();
    trackEvent('Client Resource Card - PDF Opened');
    if (contactInfoLink) {
      window.open(contactInfoLink, '_blank', 'noopener');
    }
  };

  const handleViewMoreClick = e => {
    e.stopPropagation();
    trackEvent('Client Resource Card - View More Info Clicked');
    setShowPopup(true);
  };

  const handleCopyClick = () => {
    trackEvent('Client Resource Card - Copy Clicked');
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const file = getImageFile(item);

  const cardRootStyle = file
    ? {
        backgroundImage: `url(${file})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }
    : {};
  if (file && grayscale) {
    cardRootStyle.filter = 'grayscale(1)';
  }

  const readMoreReferenceId = _get(readMoreReference, 'sys.id');
  const readMoreReferenceFields = _get(readMoreReference, 'fields');
  const showViewMoreButton = !!(readMoreReferenceId || readMoreReferenceFields);

  const shouldFetchContextData =
    showPopup && !!readMoreReferenceId && !readMoreReferenceFields;

  // Fetch Context data when popup is opened and we only have the ID
  const { data: contextData, isFetching: isFetchingContext } = useQuery(
    shouldFetchContextData && [
      'context-data',
      readMoreReferenceId,
      ctfLocaleFilter,
    ],
    getContextData,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  // Use either the existing fields or the fetched context data
  const finalContextData =
    readMoreReferenceFields || _get(contextData, 'fields');

  const callToAction = _get(item, 'callToAction', 'Schedule');

  const renderSecondaryCta = () => {
    if (contactInfoType === 'Copy') {
      const { html, text } =
        getTextsFromRichText(contactInfoCopy?.json || contactInfoCopy) || {};

      return (
        <>
          <CopyButton
            getHTMLContentToCopy={() => html}
            getContentToCopy={() => text}
            onCopied={handleCopyClick}
            buttonText={contactInfo}
            className={classes.copyButton}
          />
        </>
      );
    }

    if (contactInfoType === 'Link') {
      return (
        <Button
          variant="outlined"
          className={classes.ctaButton}
          onClick={handleLinkClick}
          color="primary"
        >
          {contactInfo}
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      <Card
        className={classes.card}
        padding={0}
        borderRadius={8}
        borderColor="#E9ECED"
        mb={isTopicPage ? 20 : 0}
        height="100%"
        fullHeight
        width={isTopicPage ? '100%' : ''}
        id={`item-wrapper-${slug}`}
      >
        <Box className={classes.itemWrapper}>
          <Box className={classes.cardContent}>
            <Box className={classes.imageContainer}>
              {file && (
                <div
                  aria-label={`Image for ${cardTitle}`}
                  style={cardRootStyle}
                />
              )}
            </Box>
            <Box className={classes.infoWrapper}>
              <Box className={classes.contentSection}>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  align="left"
                  className={classes.title}
                  name="title"
                >
                  {cardTitle}
                </Typography>
                {cardSubtitle && (
                  <Typography
                    color="textPrimary"
                    align="left"
                    className={classes.description}
                    name="description"
                  >
                    {cardSubtitle}
                  </Typography>
                )}
                <Box className={classes.highlightsContainer}>
                  {_get(item, 'highlights', []).map(exclusion => (
                    <div key={exclusion} className={classes.exclusionItem}>
                      <CloseIcon className={classes.exclusionIcon} />
                      <Typography className={classes.exclusionText}>
                        {exclusion}
                      </Typography>
                    </div>
                  ))}
                </Box>
              </Box>
              <Box className={classes.buttonsSection}>
                <Box className={classes.ctaWrapper}>
                  {renderSecondaryCta()}
                  <Button
                    variant="contained"
                    className={_classNames(
                      classes.ctaButton,
                      classes.scheduleButton,
                    )}
                    onClick={handleScheduleClick}
                    color="primary"
                    aria-label={`${callToAction} ${cardTitle}`}
                  >
                    <span className={classes.scheduleText}>{callToAction}</span>
                    <EmailIcon fontSize="small" />
                  </Button>
                </Box>
                {showViewMoreButton && (
                  <Button
                    className={classes.viewMoreButton}
                    aria-expanded={showPopup}
                    aria-label={`${
                      showPopup ? 'Hide' : 'Show'
                    } more information`}
                    onClick={handleViewMoreClick}
                  >
                    {_get(
                      resourceItemPageSiteCopy,
                      'pageCopy.viewMoreInfo',
                      'View More Info',
                    )}
                    <KeyboardArrowDownIcon className={classes.arrowIcon} />
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
      {showPopup && (
        <ContextInfoPopup
          open
          title={cardTitle}
          trackEvent={trackEvent}
          onClose={handleClosePopup}
          data={finalContextData}
          loading={isFetchingContext}
        />
      )}
    </>
  );
}

export default LinkItemMultiCta;
